import { GetStaticPaths, GetStaticProps } from 'next';

import {
  CollectionDetailsDocument,
  CollectionPathsDocument,
  ProductListDocument,
} from '@sdk/gqlTypes/generatedHooks';
import {
  CollectionDetailsQuery,
  CollectionDetailsQueryVariables,
  CollectionPathsQuery,
  CollectionPathsQueryVariables,
  ProductListQuery,
  ProductListQueryVariables,
} from '@sdk/gqlTypes/generatedOperations';

import { PrismicCategoryQueryDocument } from '@prismic/gqlTypes/prismicHooks';
import {
  PrismicCategoryQuery,
  PrismicCategoryQueryVariables,
} from '@prismic/gqlTypes/prismicOperations';

import {
  incrementalStaticRegenerationRevalidate,
  staticPathsFallback,
} from '@temp/constants';

import { prismicApolloClient, saleorApolloClient } from '@utils/ssg';
import { localeToPrismicQuery, localeToSaleorQuery } from '@utils/regions';
import { generateCollectionSeo } from '@utils/seo';
import { mapEdgesToItems } from '@utils/maps';

import { decodeKey, generateKey } from '@utils/core';

import { CollectionPage, CollectionPageProps } from '@temp/views/Collection';

export default CollectionPage;

export const getStaticPaths: GetStaticPaths<any> = async ({ locales }) => {
  const paths = [];

  await Promise.all(
    locales.map(async (locale) => {
      const { data } = await saleorApolloClient.query<
        CollectionPathsQuery,
        CollectionPathsQueryVariables
      >({
        query: CollectionPathsDocument,
        variables: {
          ...localeToSaleorQuery(locale),
        },
      });
      const localPaths = data.collections.edges.map(({ node }) => ({
        params: {
          slug: node.name,
          id: decodeKey(node.id, 'Collection'),
        },
        locale,
      }));
      paths.push(...localPaths);
    })
  );

  return { paths, fallback: staticPathsFallback };
};

export const getStaticProps: GetStaticProps<
  CollectionPageProps,
  CollectionPageProps['params']
> = async ({ params: { slug, id }, locale, defaultLocale, locales }) => {
  const { data: dataCollection } = await saleorApolloClient.query<
    CollectionDetailsQuery,
    CollectionDetailsQueryVariables
  >({
    query: CollectionDetailsDocument,
    variables: {
      id: generateKey(id, 'Collection'),
      ...localeToSaleorQuery(locale),
    },
  });

  if (!dataCollection?.collection) {
    return {
      notFound: true,
    };
  }

  const { data: dataProducts } = await saleorApolloClient.query<
    ProductListQuery,
    ProductListQueryVariables
  >({
    query: ProductListDocument,
    variables: {
      filter: {
        attributes: [],
        collections: dataCollection?.collection?.id
          ? [dataCollection.collection.id]
          : [],
        price: {
          gte: null,
          lte: null,
        },
      },
      first: 100,
      ...localeToSaleorQuery(locale),
      sortBy: null,
    },
  });

  const { data: dataPrismic } = await prismicApolloClient.query<
    PrismicCategoryQuery,
    PrismicCategoryQueryVariables
  >({
    query: PrismicCategoryQueryDocument,
    variables: {
      ...localeToPrismicQuery(locale),
      uid: dataCollection?.collection?.slug,
    },
  });

  const seo = await generateCollectionSeo(
    dataCollection.collection,
    locale,
    defaultLocale,
    locales
  );

  return {
    revalidate: incrementalStaticRegenerationRevalidate,
    props: {
      collection: dataPrismic?.category,
      attributes: mapEdgesToItems(dataCollection.attributes),
      products: mapEdgesToItems(dataProducts.products),
      seo,
      params: { slug, id },
      key: id,
    },
  };
};
